import { useState } from "react";

import { Card } from "../../components/Card";
import { ConfirmModal } from "../../components/ConfirmModal";
import { PageHeader } from "../../components/ui/PageHeader";
import { inputStyle } from "../../components/ui/FormField";
import { secretStatusLabel, settingsGroups } from "../../lib/settingsView";
import { useSettings, useSetSecret, useRestartService } from "../../hooks/useSettings";
import { ApiError } from "../../lib/api";
import type { SecretStatus } from "../../api/settings";

export function Settings() {
  const { data, isLoading, isError } = useSettings();

  // Restart control state (Task 8). Declared unconditionally — before the
  // early returns below — to satisfy the Rules of Hooks.
  const [confirmService, setConfirmService] = useState<string | null>(null);
  const [restartErr, setRestartErr] = useState<string | null>(null);
  const [restartedMsg, setRestartedMsg] = useState<string | null>(null);
  const restartMut = useRestartService();

  function doRestart() {
    if (!confirmService) return;
    setRestartErr(null);
    restartMut.mutate(confirmService, {
      onSuccess: (r) => {
        setConfirmService(null);
        setRestartedMsg(
          `Restart triggered for ${r.service} (deployment ${r.deployment_id || "queued"}). Allow ~1-2 min.`,
        );
      },
      onError: (e) =>
        setRestartErr(e instanceof ApiError ? mapSettingsError(e) : "Restart failed"),
    });
  }

  if (isLoading) return <StatusBlock label="Loading settings…" />;
  if (isError || !data) return <StatusBlock label="Failed to load settings." tone="error" />;

  const groups = settingsGroups(data);

  // Unique set of services owning a restart-gated secret (e.g. console-api,
  // voice-worker). Runtime secrets (Recall/HeyGen) need no restart and are
  // excluded.
  const restartServices = Array.from(
    new Set(
      data.secrets
        .filter((s) => s.take_effect === "restart")
        .flatMap((s) => s.services),
    ),
  );

  return (
    <div style={{ fontFamily: "var(--font-sans)" }}>
      <PageHeader
        eyebrow="Platform configuration & secrets"
        title="Settings"
        subtitle="Managed configuration and credential status across the platform. Secret values are never displayed — only their configuration state."
      />

      {restartServices.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 12,
            padding: "14px 16px",
            marginBottom: 18,
            background: "var(--bg-card)",
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-card)",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 240px", minWidth: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>
              Apply changes
            </span>
            <span style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5 }}>
              Secrets that aren't read at runtime take effect after the owning service restarts.
            </span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {restartServices.map((service) => (
              <button
                key={service}
                type="button"
                onClick={() => setConfirmService(service)}
                style={{
                  padding: "0 16px",
                  borderRadius: "var(--radius-input)",
                  border: "1px solid var(--cta)",
                  background: "var(--cta)",
                  color: "var(--bg-card)",
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.02em",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  minHeight: "var(--tap-min)",
                }}
              >
                {`Restart ${service}`}
              </button>
            ))}
          </div>
          {restartedMsg && (
            <span style={{ flexBasis: "100%", fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5 }}>
              {restartedMsg}
            </span>
          )}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: 14,
        }}
      >
        {groups.map((group) => (
          <Card key={group.domain} title={group.domain}>
            {group.config.map((entry) => (
              <ConfigRow key={entry.key} label={entry.label} value={entry.value} />
            ))}
            {group.secrets.map((secret) => (
              <SecretRow key={secret.key} secret={secret} />
            ))}
          </Card>
        ))}
      </div>

      <WorkerEnvFootnote items={data.worker_config_inventory} />

      <ConfirmModal
        open={confirmService !== null}
        title={`Restart ${confirmService}?`}
        description={warningFor(confirmService)}
        confirmLabel="Restart service"
        danger
        loading={restartMut.isPending}
        error={restartErr}
        onConfirm={() => void doRestart()}
        onCancel={() => {
          setConfirmService(null);
          setRestartErr(null);
        }}
      />
    </div>
  );
}

/**
 * Coupled-effect warning copy for a restart confirm, keyed by service. Falls
 * back to a generic line for any future service name.
 */
function warningFor(service: string | null): string {
  if (service === "voice-worker")
    return "Restarting the voice worker DROPS ANY ACTIVE CALLS. Restart during a quiet window.";
  if (service === "console-api")
    return "Restarting console-api applies rotated secrets. If you rotated the magic-link secret, ALL operators (including you) will be logged out.";
  return `Restarting ${service ?? "this service"} applies any pending secret changes.`;
}

/* ============================================================ rows */

function ConfigRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 16,
        padding: "8px 0",
        borderBottom: "1px dashed var(--border-subtle)",
      }}
    >
      <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{label}</span>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          color: value ? "var(--text-primary)" : "var(--text-muted)",
          textAlign: "right",
          wordBreak: "break-all",
        }}
      >
        {value || "—"}
      </span>
    </div>
  );
}

/**
 * Maps a set-secret {@link ApiError} to operator-facing copy. The backend
 * rarely 4xxs a settable key; the common case is a 503 / "not configured"
 * before the Secrets Manager IAM grant has been applied.
 */
function mapSettingsError(e: ApiError): string {
  if (e.status === 503 || e.detail.toLowerCase().includes("not configured"))
    return "Secrets backend not reachable — IAM may not be applied yet.";
  if (e.detail === "secret_not_settable") return "This secret can't be set from the UI.";
  return `Set failed: ${e.detail}`;
}

/**
 * Status of a managed secret. Read-only base (label + tone-colored status
 * badge + a hint line). For settable secrets this also renders the WRITE-ONLY
 * Set affordance: a masked, never-pre-filled input + a Set button, with a
 * coupled-effect confirm for dangerous secrets. The restart control itself is
 * Task 8 — here we only show a take-effect hint.
 */
export function SecretRow({ secret }: { secret: SecretStatus }) {
  const [draft, setDraft] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const setMut = useSetSecret();

  function doSet() {
    setErr(null);
    setMut.mutate(
      { key: secret.key, value: draft },
      {
        onSuccess: () => {
          setDraft("");
          setConfirmOpen(false);
        },
        onError: (e) =>
          setErr(e instanceof ApiError ? mapSettingsError(e) : "Set failed"),
      },
    );
  }

  function onSetClick() {
    if (!draft.trim()) {
      setErr("Enter a value first");
      return;
    }
    if (secret.coupled_warning) {
      setConfirmOpen(true);
      return;
    }
    doSet();
  }

  const setDisabled = setMut.isPending || !draft.trim();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: "10px 0",
        borderBottom: "1px dashed var(--border-subtle)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 16,
        }}
      >
        <span style={{ fontSize: 12, color: "var(--text-primary)" }}>{secret.label}</span>
        <SecretStatusBadge secret={secret} />
      </div>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          color: "var(--text-muted)",
          letterSpacing: "0.02em",
        }}
      >
        {secret.take_effect}
        {secret.services.length > 0 ? ` · ${secret.services.join("/")}` : ""}
      </span>

      {/* Slice 2: Set affordance · Slice 3: restart control */}
      {secret.settable && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "stretch" }}>
            <input
              type="password"
              style={{ ...inputStyle, flex: 1, minWidth: 0 }}
              placeholder="Set new value…"
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                if (err) setErr(null);
              }}
              autoComplete="off"
            />
            <button
              type="button"
              onClick={onSetClick}
              disabled={setDisabled}
              style={{
                padding: "0 16px",
                borderRadius: "var(--radius-input)",
                border: "1px solid var(--cta)",
                background: "var(--cta)",
                color: "var(--bg-card)",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.02em",
                cursor: setDisabled ? "not-allowed" : "pointer",
                opacity: setDisabled ? 0.5 : 1,
                whiteSpace: "nowrap",
                minHeight: "var(--tap-min)",
              }}
            >
              {setMut.isPending ? "Setting…" : "Set"}
            </button>
          </div>

          {/* Take-effect hint (the restart control lands in Task 8). */}
          <span style={{ fontSize: 10.5, color: "var(--text-muted)", lineHeight: 1.5 }}>
            {secret.take_effect === "restart"
              ? `Takes effect after a restart of: ${
                  secret.services.length > 0 ? secret.services.join(" + ") : "the affected services"
                }`
              : "Takes effect within ~1 min"}
          </span>

          {/* Inline error (suppressed while the confirm modal carries it). */}
          {err && !confirmOpen && (
            <span style={{ fontSize: 11, color: "var(--error)", lineHeight: 1.5 }}>{err}</span>
          )}
        </div>
      )}

      <ConfirmModal
        open={confirmOpen}
        title={`Rotate ${secret.label}?`}
        description={secret.coupled_warning ?? undefined}
        confirmLabel="Set & rotate"
        danger
        loading={setMut.isPending}
        error={err}
        onConfirm={() => void doSet()}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}

function SecretStatusBadge({ secret }: { secret: SecretStatus }) {
  // configured===true → success, ===false → warning (missing), ===null → muted (unknown)
  const color =
    secret.configured === null
      ? "var(--text-muted)"
      : secret.configured
        ? "var(--success)"
        : "var(--warning)";
  return (
    <span
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.08em",
        color,
        textAlign: "right",
        whiteSpace: "nowrap",
      }}
    >
      {secretStatusLabel(secret)}
    </span>
  );
}

/* ============================================================ footnote */

function WorkerEnvFootnote({ items }: { items: { key: string; note: string }[] }) {
  if (items.length === 0) return null;
  return (
    <div style={{ marginTop: 28 }}>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9.5,
          letterSpacing: "0.18em",
          color: "var(--text-muted)",
          textTransform: "uppercase",
          marginBottom: 12,
        }}
      >
        Worker env (managed via deploy)
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {items.map((item) => (
          <div
            key={item.key}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              gap: 16,
              padding: "6px 0",
              borderBottom: "1px dashed var(--border-subtle)",
            }}
          >
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "var(--text-secondary)",
                letterSpacing: "0.02em",
                wordBreak: "break-all",
              }}
            >
              {item.key}
            </span>
            <span
              style={{
                fontSize: 11,
                color: "var(--text-muted)",
                textAlign: "right",
                maxWidth: 420,
                lineHeight: 1.5,
              }}
            >
              {item.note}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusBlock({ label, tone }: { label: string; tone?: "error" }) {
  return (
    <div
      style={{
        padding: "32px 24px",
        textAlign: "center",
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-card)",
        color: tone === "error" ? "var(--error)" : "var(--text-muted)",
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </div>
  );
}
